// Temporary simple dashboard replacement
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const SimpleLawyerDashboard = ({ backendUrl, setCurrentView }) => {
  const [cases, setCases] = useState([]);
  const [lawyerPackage, setLawyerPackage] = useState(null);
  const [filteredCases, setFilteredCases] = useState([]);
  const [takenCases, setTakenCases] = useState([]);
  const [takenCasesPage, setTakenCasesPage] = useState(0);
  const [takenCasesSearch, setTakenCasesSearch] = useState('');
  const [showTakenCaseDetailsModal, setShowTakenCaseDetailsModal] = useState(false);
  const [selectedTakenCase, setSelectedTakenCase] = useState(null); // Caso tomado seleccionado
  const [filters, setFilters] = useState({
    materia: '',
    ordenamiento: 'ia_score',
    region: ''
  });
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showViewProfileModal, setShowViewProfileModal] = useState(false); // NEW: Ver perfil sin costo
  const [takingCase, setTakingCase] = useState(null); // Track which case is being taken
  const [showTakeCaseModal, setShowTakeCaseModal] = useState(false);
  const [selectedPackageForPayPal, setSelectedPackageForPayPal] = useState(null); // Para PayPal Buttons
  const [selectedCase, setSelectedCase] = useState(null);
  const [showCaseDetailsModal, setShowCaseDetailsModal] = useState(false);
  const [paymentSuccessNotification, setPaymentSuccessNotification] = useState(null); // Notification for payment success
  
  // Get lawyer data from localStorage and state management
  const [lawyerData, setLawyerData] = useState(() => {
    const lawyerDataStr = localStorage.getItem('lawyer_data');
    if (lawyerDataStr) {
      try {
        return JSON.parse(lawyerDataStr);
      } catch (e) {
        console.error('Error parsing lawyer_data:', e);
      }
    }
    return {};
  });
  
  // Function to validate token and maintain session  
  const validateSession = useCallback(async () => {
    const token = localStorage.getItem('lawyer_token');
    const lawyerDataStr = localStorage.getItem('lawyer_data');
    
    if (!token || !lawyerDataStr) {
      console.log('No valid session found, redirecting to login');
      setCurrentView('lawyer-login');
      return false;
    }
    
    // For now, just trust the localStorage token (we can add server validation later)
    // This prevents unnecessary API calls during initial dashboard load
    console.log('Session validation: Valid token found');
    return true;
  }, [setCurrentView]);
  
  // Function to refresh lawyer data from backend
  const refreshLawyerData = async () => {
    try {
      const token = localStorage.getItem('lawyer_token');
      const lawyerId = localStorage.getItem('lawyer_id') || lawyerData.id;
      
      if (!token || !lawyerId) return;

      // Fetch updated package status with credits
      const response = await fetch(`${backendUrl}/api/lawyers/${lawyerId}/package-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const packageData = await response.json();
        
        // Update lawyerData with fresh credits and plan info
        const updatedLawyerData = {
          ...lawyerData,
          credits_remaining: packageData.credits_remaining,
          plan_type: packageData.plan_type,
          total_cases_taken: packageData.total_cases_taken,
          cases_this_month: packageData.cases_this_month,
          package_purchased_date: packageData.package_purchased_date
        };
        
        // Update both state and localStorage
        setLawyerData(updatedLawyerData);
        localStorage.setItem('lawyer_data', JSON.stringify(updatedLawyerData));
        
        console.log('Lawyer data refreshed with credits:', packageData.credits_remaining);
      } else {
        console.error('Error fetching package status:', response.status);
      }
    } catch (error) {
      console.error('Error refreshing lawyer data:', error);
    }
  };
  
  const [packages] = useState([
    { 
      id: 'pack_1', 
      name: '1 Caso Individual', 
      price: 4.70, 
      originalPrice: null,
      cases: 1, 
      color: 'blue',
      discount: null,
      savings: null,
      badge: 'INDIVIDUAL'
    },
    { 
      id: 'pack_3', 
      name: '3 Casos', 
      price: 12.69, 
      originalPrice: 14.10,
      cases: 3, 
      color: 'green',
      discount: '10%',
      savings: 1.41,
      badge: 'POPULAR'
    },
    { 
      id: 'pack_6', 
      name: '6 Casos', 
      price: 23.97, 
      originalPrice: 28.20,
      cases: 6, 
      color: 'purple',
      discount: '15%',
      savings: 4.23,
      badge: 'MEJOR VALOR'
    },
    { 
      id: 'pack_12', 
      name: '12 Casos', 
      price: 45.12, 
      originalPrice: 56.40,
      cases: 12, 
      color: 'orange',
      discount: '20%',
      savings: 11.28,
      badge: 'PROFESIONAL'
    }
  ]);

  // Check for payment success on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const paymentSuccess = urlParams.get('payment_success');
    const creditsAdded = urlParams.get('credits');
    
    // Handle new payment_success parameter (from PayPal return)
    if (paymentSuccess === 'true') {
      // Show success notification with credits info
      const creditsMsg = creditsAdded ? `+${creditsAdded} créditos agregados` : 'Créditos actualizados';
      setPaymentSuccessNotification(creditsMsg);
      
      // Refresh lawyer data to show new credits
      setTimeout(() => {
        fetchLawyerDataAfterPayment();
      }, 500);
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setPaymentSuccessNotification(null);
      }, 5000);
      
      // Clean URL parameters
      window.history.replaceState({}, document.title, window.location.pathname + '?view=lawyer-dashboard');
    }
    // Handle legacy payment parameter
    else if (paymentStatus === 'success') {
      // Payment successful, refresh lawyer data
      setTimeout(() => {
        alert('¡Pago procesado exitosamente! Tus créditos han sido actualizados.');
        fetchLawyerDataAfterPayment();
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 1000);
    } else if (paymentStatus === 'cancelled') {
      alert('Pago cancelado. No se han realizado cambios.');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchLawyerDataAfterPayment = async () => {
    try {
      const token = localStorage.getItem('lawyer_token');
      if (!token) return;

      // Fetch updated lawyer data from backend
      const response = await fetch(`${backendUrl}/api/lawyers/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const updatedData = await response.json();
        // Update localStorage with fresh data including credits
        localStorage.setItem('lawyer_data', JSON.stringify(updatedData));
        // Refresh page to update UI
        window.location.reload();
      }
    } catch (error) {
      console.error('Error fetching updated lawyer data:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First validate session before fetching data
        const isSessionValid = await validateSession();
        if (!isSessionValid) {
          return; // Session invalid, user will be redirected to login
        }

        const token = localStorage.getItem('lawyer_token');
        if (!token) {
          console.error('No token found');
          setCurrentView('lawyer-login');
          return;
        }
        
        // First, refresh lawyer data with current credits
        await refreshLawyerData();
        
        // Fetch taken cases (NEW) - Using taken_cases array with complete case info
        const takenCasesResponse = await axios.get(`${backendUrl}/api/lawyers/${lawyerData.id}/package-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (takenCasesResponse.data.taken_cases) {
          setTakenCases(takenCasesResponse.data.taken_cases);
          console.log('📋 Taken cases fetched:', takenCasesResponse.data.taken_cases.length, 'cases');
        }
        
        // Fetch cases
        const casesResponse = await axios.get(`${backendUrl}/api/cases`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Backend returns {cases: [...], pagination: {...}}
        const casesData = Array.isArray(casesResponse.data?.cases) ? casesResponse.data.cases : [];
        console.log('📋 Cases fetched:', casesData.length, 'cases');
        console.log('🔍 First case sample:', casesData[0] ? Object.keys(casesData[0]) : 'No cases');
        console.log('🔍 Sample case data:', casesData[0]);
        setCases(casesData);
        setFilteredCases(casesData);

        // Package status is now handled by refreshLawyerData()
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [backendUrl]);

  // Handle ESC key for closing modals
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape') {
        setShowTakeCaseModal(false);
        setShowPackageModal(false);
        setShowProfileModal(false);
        setShowCaseDetailsModal(false);
      }
    };

    if (showTakeCaseModal || showPackageModal || showProfileModal || showCaseDetailsModal) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [showTakeCaseModal, showPackageModal, showProfileModal, showCaseDetailsModal]);

  // Filter cases based on selected filters
  useEffect(() => {
    if (!Array.isArray(cases)) {
      setFilteredCases([]);
      return;
    }

    let filtered = [...cases];
    
    if (filters.materia) {
      filtered = filtered.filter(caso => caso.area_legal === filters.materia);
    }
    
    if (filters.region) {
      filtered = filtered.filter(caso => caso.region === filters.region);  
    }
    
    // Sort cases
    filtered.sort((a, b) => {
      switch (filters.ordenamiento) {
        case 'ia_score':
          return (b.quality_score?.quality_score || 0) - (a.quality_score?.quality_score || 0);
        case 'verification':
          return (b.verification_score?.total_score || 0) - (a.verification_score?.total_score || 0);
        case 'fecha':
          return new Date(b.created_at) - new Date(a.created_at);
        default:
          return 0;
      }
    });
    
    setFilteredCases(filtered);
  }, [cases, filters]);

  const handlePackagePurchase = async (packageData, paymentMethod) => {
    try {
      const token = localStorage.getItem('lawyer_token');
      
      if (!token) {
        alert('Sesión expirada. Por favor inicia sesión nuevamente.');
        return;
      }
      
      console.log('Attempting package purchase with token:', token ? 'Present' : 'Missing');
      
      if (paymentMethod === 'paypal') {
        // PayPal payment - con return_url y cancel_url requeridos
        const response = await axios.post(`${backendUrl}/api/lawyers/purchase-package`, {
          package_type: packageData.id,
          return_url: `${window.location.origin}/?payment=success`,
          cancel_url: `${window.location.origin}/?payment=cancelled`
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('PayPal response:', response.data);
        
        if (response.data.approval_url) {
          window.open(response.data.approval_url, '_blank');
          setShowPackageModal(false);
          // Show success message
          alert('Redirigiendo a PayPal para completar el pago...');
        } else if (response.data.order_id) {
          // PayPal order created but no approval URL
          alert('Orden de pago creada. Por favor contacta soporte si no se abre PayPal.');
        }
      } else if (paymentMethod === 'mercadopago') {
        // MercadoPago payment - obtener lawyer_id del localStorage
        const lawyerData = JSON.parse(localStorage.getItem('lawyer_data') || '{}');
        const lawyerId = lawyerData.id || localStorage.getItem('lawyer_id') || 'temp_lawyer_id';
        
        const response = await axios.post(`${backendUrl}/api/payments/mercadopago/create-preference`, {
          case_id: 'package_purchase', // Special identifier for packages
          lawyer_id: lawyerId, // Real lawyer ID from localStorage
          package_type: packageData.id,
          amount: packageData.price
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.init_point) {
          window.open(response.data.init_point, '_blank');
          setShowPackageModal(false);
          // Show success message
          alert('Redirigiendo a MercadoPago para completar el pago...');
        } else {
          alert('Error: No se pudo generar el enlace de pago de MercadoPago.');
        }
      }
    } catch (error) {
      console.error('Error purchasing package:', error);
      
      // Better error handling - show specific error if available
      let errorMessage = 'Error al procesar el pago. Intenta nuevamente.';
      
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleProfileUpdatePayment = async (paymentMethod) => {
    try {
      const token = localStorage.getItem('lawyer_token');
      
      if (!token) {
        alert('Sesión expirada. Por favor inicia sesión nuevamente.');
        return;
      }
      
      const profileUpdatePrice = 19; // $19 USD para modificar perfil
      
      if (paymentMethod === 'paypal') {
        // PayPal payment para modificar perfil
        const response = await axios.post(`${backendUrl}/api/payments/paypal/create-profile-update-order`, {
          return_url: `${window.location.origin}/?payment=profile-success`,
          cancel_url: `${window.location.origin}/?payment=profile-cancelled`
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('PayPal profile update response:', response.data);
        
        if (response.data.approval_url) {
          window.open(response.data.approval_url, '_blank');
          setShowProfileModal(false);
          alert('Redirigiendo a PayPal para pagar la actualización de perfil...');
        } else if (response.data.order_id) {
          alert('Orden de pago creada. Por favor contacta soporte si no se abre PayPal.');
        }
      } else if (paymentMethod === 'mercadopago') {
        // MercadoPago payment para modificar perfil
        const lawyerData = JSON.parse(localStorage.getItem('lawyer_data') || '{}');
        const lawyerId = lawyerData.id || localStorage.getItem('lawyer_id') || 'temp_lawyer_id';
        
        const response = await axios.post(`${backendUrl}/api/payments/mercadopago/create-preference`, {
          case_id: 'profile_update', // Special identifier for profile updates
          lawyer_id: lawyerId,
          package_type: 'profile_update',
          amount: profileUpdatePrice
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.init_point) {
          window.open(response.data.init_point, '_blank');
          setShowProfileModal(false);
          alert('Redirigiendo a MercadoPago para pagar la actualización de perfil...');
        } else {
          alert('Error: No se pudo generar el enlace de pago de MercadoPago.');
        }
      }
    } catch (error) {
      console.error('Error processing profile update payment:', error);
      
      let errorMessage = 'Error al procesar el pago para actualizar perfil. Intenta nuevamente.';
      
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  // Take Case Functions
  const handleTakeCase = (caso) => {
    setSelectedCase(caso);
    setShowTakeCaseModal(true);
  };

  const handleViewDetails = (caso) => {
    setSelectedCase(caso);
    setShowCaseDetailsModal(true);
  };

  const analyzeAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) {
      return { total: 0, analysis: "No se adjuntaron documentos" };
    }

    // Categories for document analysis
    const categories = {
      contracts: ['contrato', 'convenio', 'acuerdo'],
      legal: ['demanda', 'sentencia', 'recurso', 'apelacion'],
      financial: ['factura', 'boleta', 'comprobante', 'pago'],
      identity: ['cedula', 'rut', 'carnet', 'identidad'],
      correspondence: ['email', 'carta', 'notificacion', 'oficio']
    };

    const categoryCounts = {
      contracts: 0,
      legal: 0,
      financial: 0,
      identity: 0,
      correspondence: 0,
      other: 0
    };

    // Analyze each attachment
    attachments.forEach(attachment => {
      const filename = attachment.name ? attachment.name.toLowerCase() : '';
      let categorized = false;

      Object.keys(categories).forEach(category => {
        if (!categorized) {
          categories[category].forEach(keyword => {
            if (filename.includes(keyword)) {
              categoryCounts[category]++;
              categorized = true;
            }
          });
        }
      });

      if (!categorized) {
        categoryCounts.other++;
      }
    });

    // Generate analysis text
    let analysis = '';
    if (attachments.length > 0) {
      analysis += `Total: ${attachments.length} documento${attachments.length > 1 ? 's' : ''}\n`;
      
      Object.keys(categoryCounts).forEach(category => {
        const count = categoryCounts[category];
        if (count > 0 && category !== 'other') {
          const categoryNames = {
            contracts: 'contratos',
            legal: 'documentos legales',
            financial: 'comprobantes financieros',
            identity: 'documentos de identidad',
            correspondence: 'correspondencia'
          };
          analysis += `• ${count} ${categoryNames[category]} relacionado${count > 1 ? 's' : ''}\n`;
        }
      });
      analysis += '• Documentación parece relevante para el caso';
    } else {
      analysis += '• Documentos generales adjuntos';
    }

    return { total: attachments.length, analysis };
  };

  // Función para calcular días restantes hasta expiración
  const calculateDaysRemaining = (caso) => {
    if (!caso.expires_at && !caso.created_at) return null;
    
    try {
      let expiryDate;
      
      if (caso.expires_at) {
        // Si existe expires_at, usarlo directamente
        expiryDate = new Date(caso.expires_at);
      } else if (caso.created_at) {
        // Si solo existe created_at, agregar 7 días
        expiryDate = new Date(caso.created_at);
        expiryDate.setDate(expiryDate.getDate() + 7);
      }
      
      if (isNaN(expiryDate.getTime())) return null;
      
      const now = new Date();
      const diffTime = expiryDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      console.error('Error calculating days remaining:', error);
      return null;
    }
  };

  // Función para generar badge de expiración
  const getExpirationBadge = (daysRemaining) => {
    if (daysRemaining === null || daysRemaining < 0) return null;
    
    let badgeClass = "";
    let icon = "⏰";
    let text = "";
    
    if (daysRemaining === 0) {
      badgeClass = "bg-gradient-to-r from-red-500 to-red-600 text-white animate-pulse";
      icon = "🚨";
      text = "EXPIRA HOY";
    } else if (daysRemaining === 1) {
      badgeClass = "bg-gradient-to-r from-orange-500 to-red-500 text-white";
      icon = "⚠️";
      text = "1 día restante";
    } else if (daysRemaining <= 2) {
      badgeClass = "bg-gradient-to-r from-yellow-500 to-orange-500 text-white";
      icon = "⏰";
      text = `${daysRemaining} días restantes`;
    } else if (daysRemaining <= 5) {
      badgeClass = "bg-gradient-to-r from-blue-500 to-indigo-500 text-white";
      icon = "📅";
      text = `${daysRemaining} días restantes`;
    } else {
      badgeClass = "bg-gradient-to-r from-green-500 to-emerald-500 text-white";
      icon = "🆕";
      text = `${daysRemaining} días restantes`;
    }
    
    return { badgeClass, icon, text };
  };

  const takeCaseWithCredits = async () => {
    if (!selectedCase) return;
    
    setTakingCase(selectedCase.id);
    try {
      const token = localStorage.getItem('lawyer_token');
      const response = await fetch(`${backendUrl}/api/lawyers/take-case`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          case_id: selectedCase.id,
          payment_type: 'credit'
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert('¡Caso tomado exitosamente! Contacta al cliente con la información proporcionada.');
        
        // Refresh lawyer data to get updated credits
        await refreshLawyerData();
        
        // CORRECCIÓN CRÍTICA: Actualizar lista dinámicamente en lugar de recargar página
        // Eliminar el caso tomado de la lista local inmediatamente
        const updatedCases = cases.filter(caso => caso.id !== selectedCase.id);
        setCases(updatedCases);
        
        // También actualizar la lista filtrada si está activa
        const updatedFilteredCases = filteredCases.filter(caso => caso.id !== selectedCase.id);
        setFilteredCases(updatedFilteredCases);
        
        // Agregar caso a la lista de casos tomados si existe
        if (selectedCase) {
          setTakenCases(prev => [...prev, selectedCase]);
        }
        
        console.log(`✅ Caso ${selectedCase.id} eliminado dinámicamente del dashboard`);
      } else {
        const error = await response.json();
        alert('Error: ' + error.detail);
      }
    } catch (error) {
      console.error('Error taking case:', error);
      alert('Error al tomar el caso: ' + error.message);
    }
    setTakingCase(null);
    setShowTakeCaseModal(false);
  };

  const takeCaseWithPayment = async () => {
    if (!selectedCase) return;
    
    setTakingCase(selectedCase.id);
    try {
      const token = localStorage.getItem('lawyer_token');
      const lawyerId = localStorage.getItem('lawyer_id');
      
      // 🔒 CORRECCIÓN: Redirigir a PayPal para pago real antes de dar el caso
      // Crear orden de pago para caso individual
      const response = await fetch(`${backendUrl}/api/lawyers/purchase-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          lawyer_id: lawyerId,
          package_type: 'pack_1',  // Paquete de 1 caso
          case_id: selectedCase.id  // Caso específico a tomar después del pago
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_url) {
          // Redirigir a PayPal para completar el pago
          window.location.href = data.payment_url;
        } else {
          alert('Error: No se pudo generar el link de pago');
        }
      } else {
        const error = await response.json();
        alert('Error: ' + (error.detail || 'No se pudo procesar el pago'));
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert('Error al iniciar el pago: ' + error.message);
    }
    setTakingCase(null);
    setShowTakeCaseModal(false);
  };

  const fetchPackageStatus = async () => {
    try {
      const token = localStorage.getItem('lawyer_token');
      
      if (!token) {
        console.log('No token available for package status');
        return;
      }
      
      console.log('Fetching package status with token:', token ? 'Present' : 'Missing');
      
      const response = await axios.get(`${backendUrl}/api/lawyers/package-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLawyerPackage(response.data);
    } catch (error) {
      console.error('Error fetching package status:', error);
      if (error.response && error.response.status === 401) {
        console.log('Authentication failed for package status');
        // Don't show alert for this, just log it
      }
    }
  };

  // 🔥 NUEVAS FUNCIONES PARA CASOS TOMADOS
  
  // Función para obtener detalles completos de caso tomado
  const fetchTakenCaseDetails = async (caseId) => {
    try {
      const token = localStorage.getItem('lawyer_token');
      
      // Obtener detalles completos del caso
      const response = await axios.get(`${backendUrl}/api/lawyers/taken-case/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error('Error obteniendo detalles del caso');
      }
    } catch (error) {
      console.error('Error fetching taken case details:', error);
      alert('Error al cargar los detalles del caso: ' + error.message);
      return null;
    }
  };

  // Manejar clic en "Ver Detalles" de caso tomado
  const handleViewTakenCaseDetails = async (contact) => {
    try {
      const caseDetails = await fetchTakenCaseDetails(contact.case_id);
      if (caseDetails) {
        setSelectedTakenCase({
          ...caseDetails,
          contact_info: contact // Información del contacto tomado
        });
        setShowTakenCaseDetailsModal(true);
      }
    } catch (error) {
      console.error('Error opening taken case details:', error);
      alert('Error al abrir los detalles del caso');
    }
  };

  // Función para generar links de contacto
  const generateContactLinks = (caseDetails) => {
    const phone = caseDetails.phone || '';
    const email = caseDetails.email || '';
    const clientName = caseDetails.name || 'Cliente';
    
    const whatsappLink = phone ? 
      `https://wa.me/56${phone.replace(/[^\d]/g, '')}?text=Hola%20${encodeURIComponent(clientName)}%2C%20soy%20su%20abogado%20asignado%20para%20el%20caso%20de%20${encodeURIComponent(caseDetails.area_legal || 'asunto legal')}.%20¿Cuándo%20podemos%20conversar%3F` 
      : null;
    
    const emailLink = email ? 
      `mailto:${email}?subject=Contacto%20Profesional%20-%20Caso%20${encodeURIComponent(caseDetails.area_legal || 'Legal')}&body=Estimado/a%20${encodeURIComponent(clientName)},%0A%0ASoy%20su%20abogado%20asignado%20para%20el%20caso%20de%20${encodeURIComponent(caseDetails.area_legal || 'asunto legal')}.%20Me%20gustar%C3%ADa%20coordinar%20una%20reuni%C3%B3n%20para%20revisar%20su%20situaci%C3%B3n.%0A%0ASaludos%20cordiales`
      : null;
    
    const callLink = phone ? `tel:+56${phone.replace(/[^\d]/g, '')}` : null;
    
    return { whatsappLink, emailLink, callLink };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Payment Success Notification */}
      {paymentSuccessNotification && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center space-x-3 max-w-md">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-bold text-lg">¡Pago Exitoso!</p>
              <p className="text-sm text-green-50">{paymentSuccessNotification}</p>
            </div>
            <button 
              onClick={() => setPaymentSuccessNotification(null)}
              className="flex-shrink-0 text-white hover:text-green-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {/* Header estilo NexoAbogados - Azul profesional */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            {/* Logo y nombre */}
            <div className="flex items-center space-x-3">
              <div className="bg-white/10 backdrop-blur text-white rounded-full w-11 h-11 flex items-center justify-center font-semibold text-sm">
                {lawyerData.name ? 
                  lawyerData.name.split(' ').map(n => n[0]).join('').slice(0,2) : 
                  'AL'}
              </div>
              <div>
                <h1 className="text-white font-semibold text-lg">
                  {lawyerData.name || 'Abogado'}
                </h1>
                <p className="text-blue-200 text-sm">
                  {lawyerData.areas_legales ? lawyerData.areas_legales.slice(0,2).join(' • ') : 'Especialista Legal'}
                </p>
              </div>
            </div>
            
            {/* Stats en header */}
            <div className="flex items-center gap-6">
              {lawyerPackage && (
                <div className="hidden md:flex items-center gap-5 text-white">
                  <div className="text-center">
                    <div className="text-2xl font-bold">{lawyerPackage.credits_remaining || 0}</div>
                    <div className="text-xs text-blue-200">Créditos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{lawyerPackage.total_cases_taken || 0}</div>
                    <div className="text-xs text-blue-200">Casos tomados</div>
                  </div>
                </div>
              )}
              
              {/* Botones */}
              <div className="flex gap-2">
                <button 
                  onClick={() => setShowPackageModal(true)}
                  className="bg-green-500 hover:bg-green-400 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-green-500/25"
                >
                  Comprar Créditos
                </button>
                <button 
                  onClick={() => setShowViewProfileModal(true)}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  Mi Perfil
                </button>
                <button 
                  onClick={() => setShowProfileModal(true)}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  Modificar
                </button>
                <button 
                  onClick={() => {
                    localStorage.removeItem('lawyer_token');
                    setCurrentView('home');
                  }}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-2.5 rounded-lg text-sm transition-colors"
                >
                  Salir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Fondo claro */}
      <div className="bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Banner de créditos en móvil */}
        {lawyerPackage && (
          <div className="md:hidden bg-white rounded-xl p-4 mb-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-500">Créditos disponibles</div>
                <div className="text-3xl font-bold text-blue-900">{lawyerPackage.credits_remaining || 0}</div>
              </div>
              <button 
                onClick={() => setShowPackageModal(true)}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              >
                Comprar más
              </button>
            </div>
          </div>
        )}

        {/* Filtros estilo NexoAbogados */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Filtrar casos</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <select
                  value={filters.materia}
                  onChange={(e) => setFilters({...filters, materia: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700"
                >
                  <option value="">Todas las áreas legales</option>
                  <option value="Familia">Derecho de Familia</option>
                  <option value="Laboral">Derecho Laboral</option>
                  <option value="Civil">Derecho Civil</option>
                  <option value="Penal">Derecho Penal</option>
                  <option value="Comercial">Derecho Comercial</option>
                  <option value="Inmobiliario">Derecho Inmobiliario</option>
                </select>
                
                <select
                  value={filters.region}
                  onChange={(e) => setFilters({...filters, region: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700"
                >
                  <option value="">Todas las regiones</option>
                  <option value="Región Metropolitana">Región Metropolitana</option>
                  <option value="Región de Valparaíso">Región de Valparaíso</option>
                  <option value="Región del Biobío">Región del Biobío</option>
                </select>
                
                <select
                  value={filters.ordenamiento}
                  onChange={(e) => setFilters({...filters, ordenamiento: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700"
                >
                  <option value="ia_score">Ordenar por: Puntaje IA</option>
                  <option value="verification">Ordenar por: Verificación</option>
                  <option value="fecha">Ordenar por: Más recientes</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-center px-4 py-2 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-900">{Array.isArray(filteredCases) ? filteredCases.length : 0}</div>
                <div className="text-xs text-blue-600">casos disponibles</div>
              </div>
              <button 
                onClick={() => setFilters({materia: '', ordenamiento: 'ia_score', region: ''})}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>

      {/* MIS CASOS TOMADOS - Estilo NexoAbogados */}
      {takenCases && takenCases.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Mis Casos Asignados</h2>
            <span className="bg-green-100 text-green-700 text-sm font-medium px-3 py-1 rounded-full">{takenCases.length} casos</span>
          </div>

          {/* Search within taken cases */}
          {takenCases.length > 5 && (
            <div className="mb-4">
              <input
                type="text"
                placeholder="Buscar por nombre, área o descripción..."
                value={takenCasesSearch}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="taken-cases-search"
                onChange={(e) => { setTakenCasesSearch(e.target.value); setTakenCasesPage(0); }}
              />
            </div>
          )}

          <div data-testid="taken-cases-container" className="space-y-0">
            {/* Table header */}
            <div className="hidden md:grid md:grid-cols-12 gap-2 px-4 py-2 bg-gray-100 rounded-t-lg text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <div className="col-span-2">Área</div>
              <div className="col-span-3">Cliente</div>
              <div className="col-span-3">Descripción</div>
              <div className="col-span-2">Fecha</div>
              <div className="col-span-2 text-center">Acciones</div>
            </div>

            {/* Table rows - filtered and paginated */}
            {(() => {
              const CASES_PER_PAGE = 10;
              const searchTerm = takenCasesSearch.toLowerCase();
              const filtered = searchTerm 
                ? takenCases.filter(c => 
                    (c.client_name || '').toLowerCase().includes(searchTerm) ||
                    (c.area_legal || '').toLowerCase().includes(searchTerm) ||
                    (c.case_description || '').toLowerCase().includes(searchTerm)
                  )
                : takenCases;
              const totalPages = Math.ceil(filtered.length / CASES_PER_PAGE);
              const safePage = Math.min(takenCasesPage, Math.max(0, totalPages - 1));
              const paginatedCases = filtered.slice(safePage * CASES_PER_PAGE, (safePage + 1) * CASES_PER_PAGE);

              return (
                <>
                  {paginatedCases.map((contact, index) => (
                    <details 
                      key={index} 
                      className="group border-b border-gray-100 last:border-b-0"
                      data-testid={`taken-case-row-${index}`}
                    >
                      {/* Collapsed row */}
                      <summary className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors items-center list-none [&::-webkit-details-marker]:hidden">
                        <div className="md:col-span-2">
                          <span className="text-xs font-semibold text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full">
                            {contact.area_legal || 'Legal'}
                          </span>
                        </div>
                        <div className="md:col-span-3 font-medium text-gray-900 text-sm truncate">
                          {contact.client_name || 'Cliente'}
                        </div>
                        <div className="md:col-span-3 text-sm text-gray-500 truncate">
                          {contact.case_description || 'Sin descripción'}
                        </div>
                        <div className="md:col-span-2 text-xs text-gray-400">
                          {new Date(contact.contact_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                        <div className="md:col-span-2 flex justify-center gap-2">
                          <span className="text-xs text-blue-600 group-open:hidden">Ver +</span>
                          <span className="text-xs text-gray-400 hidden group-open:inline">Cerrar</span>
                        </div>
                      </summary>

                      {/* Expanded details */}
                      <div className="px-4 pb-4 pt-1 bg-blue-50/40 border-l-4 border-blue-500 ml-2 mr-2 mb-2 rounded-b-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Left: Case info */}
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed mb-3">
                              {contact.case_description || 'Sin descripción del caso'}
                            </p>
                            {contact.assigned_by === 'fifo_system' || contact.assigned_by === 'fifo_purchase' ? (
                              <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                Asignado automáticamente
                              </span>
                            ) : null}
                          </div>
                          
                          {/* Right: Contact info + actions */}
                          <div className="space-y-2">
                            {contact.client_email && (
                              <a href={`mailto:${contact.client_email}`} className="flex items-center text-sm text-gray-700 hover:text-blue-600 transition-colors">
                                <svg className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                                {contact.client_email}
                              </a>
                            )}
                            {contact.client_phone && (
                              <a href={`tel:${contact.client_phone}`} className="flex items-center text-sm text-gray-700 hover:text-blue-600 transition-colors">
                                <svg className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                                {contact.client_phone}
                              </a>
                            )}
                            <div className="flex gap-2 pt-2">
                              <button 
                                onClick={() => handleViewTakenCaseDetails(contact)}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-2 px-3 rounded-lg transition-colors"
                                data-testid={`view-case-details-${index}`}
                              >
                                Ver detalles completos
                              </button>
                              {contact.client_phone && (
                                <a 
                                  href={`https://wa.me/${contact.client_phone.replace(/[^\d+]/g, '')}?text=${encodeURIComponent('Hola, soy su abogado asignado por AyudaLegal. ¿Cuándo podemos conversar sobre su caso?')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium py-2 px-3 rounded-lg transition-colors"
                                  data-testid={`whatsapp-client-${index}`}
                                >
                                  WhatsApp
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </details>
                  ))}

                  {/* Pagination */}
                  {filtered.length > CASES_PER_PAGE && (
                    <div className="flex items-center justify-between pt-4 px-4 border-t border-gray-200 mt-2">
                      <span className="text-xs text-gray-500">
                        Mostrando {safePage * CASES_PER_PAGE + 1}-{Math.min((safePage + 1) * CASES_PER_PAGE, filtered.length)} de {filtered.length}
                      </span>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => setTakenCasesPage(Math.max(0, safePage - 1))}
                          disabled={safePage === 0}
                          className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-300 disabled:opacity-30 hover:bg-gray-100 transition-colors"
                          data-testid="taken-cases-prev"
                        >
                          Anterior
                        </button>
                        <span className="px-3 py-1 text-xs text-gray-600">{safePage + 1} / {totalPages}</span>
                        <button 
                          onClick={() => setTakenCasesPage(Math.min(totalPages - 1, safePage + 1))}
                          disabled={safePage >= totalPages - 1}
                          className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-300 disabled:opacity-30 hover:bg-gray-100 transition-colors"
                          data-testid="taken-cases-next"
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  )}

                  {filtered.length === 0 && takenCasesSearch && (
                    <div className="text-center py-4 text-sm text-gray-400">
                      No se encontraron casos para "{takenCasesSearch}"
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* CASOS DISPONIBLES - Estilo NexoAbogados */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Casos Disponibles</h2>
        
        {Array.isArray(filteredCases) && filteredCases.map((caso, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            {/* Header del caso */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-blue-800 bg-blue-100 px-3 py-1 rounded-full">
                    {caso.area_legal}
                  </span>
                  {caso.urgency === 'Alta' && (
                    <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-1 rounded-full">
                      Urgente
                    </span>
                  )}
                  {(caso.verification_score?.total_score || 0) >= 80 && (
                    <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                      ✓ Verificado
                    </span>
                  )}
                  {(() => {
                    const daysRemaining = calculateDaysRemaining(caso);
                    return daysRemaining !== null && daysRemaining <= 3 ? (
                      <span className="text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-1 rounded-full">
                        {daysRemaining} días restantes
                      </span>
                    ) : null;
                  })()}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{caso.comuna}, {caso.region}</h3>
              </div>
              
              {/* Score */}
              <div className="flex items-center gap-4">
                <div className="text-center bg-blue-50 rounded-xl px-4 py-2">
                  <div className="text-2xl font-bold text-blue-900">{caso.quality_score?.quality_score || 0}</div>
                  <div className="text-xs text-blue-600">Score IA</div>
                </div>
              </div>
            </div>
            
            {/* Descripción */}
            <p className="text-gray-600 mb-4 leading-relaxed">{caso.description}</p>
            
            {/* Verificaciones */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${caso.email_verified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {caso.email_verified ? '✓' : '○'} Email verificado
              </span>
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${caso.whatsapp_confirmed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {caso.whatsapp_confirmed ? '✓' : '○'} WhatsApp
              </span>
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${caso.has_curp ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {caso.has_curp ? '✓' : '○'} CURP
              </span>
            </div>
            
            {/* Info adicional y botones */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                  {new Date(caso.created_at).toLocaleDateString('es-CL')}
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                  {caso.participated_lawyers || 0} interesados
                </span>
                <span>{caso.remote_consultation ? '🖥️ Remoto' : '📍 Presencial'}</span>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => handleViewDetails(caso)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  Ver detalles
                </button>
                <button 
                  onClick={() => handleTakeCase(caso)}
                  disabled={takingCase === caso.id}
                  className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    takingCase === caso.id 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/25'
                  }`}
                >
                  {takingCase === caso.id ? 'Procesando...' : 'Tomar caso'}
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {(!Array.isArray(filteredCases) || filteredCases.length === 0) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay casos disponibles</h3>
            <p className="text-gray-500">Intenta ajustar los filtros o vuelve más tarde.</p>
          </div>
        )}
      </div>
      </div>
      </div>

      {/* Package Purchase Modal - Estilo NexoAbogados */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 rounded-t-2xl text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-1">Comprar Créditos</h2>
                  <p className="text-blue-200">Accede a casos verificados por nuestra IA</p>
                </div>
                <button 
                  onClick={() => setShowPackageModal(false)}
                  className="text-white/70 hover:text-white text-3xl font-light transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Paquetes Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {packages.map((pkg) => (
                  <div key={pkg.id} className="relative group">
                    {/* Badge de Descuento */}
                    {pkg.discount && (
                      <div className="absolute -top-4 -right-4 bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg z-10 transform rotate-12">
                        {pkg.discount} OFF
                      </div>
                    )}
                    
                    {/* Card Principal */}
                    <div className={`
                      relative border-2 rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2
                      ${pkg.color === 'blue' ? 'border-blue-200 hover:border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100' :
                        pkg.color === 'green' ? 'border-green-200 hover:border-green-400 bg-gradient-to-br from-green-50 to-green-100' :
                        pkg.color === 'purple' ? 'border-purple-200 hover:border-purple-400 bg-gradient-to-br from-purple-50 to-purple-100' :
                        'border-orange-200 hover:border-orange-400 bg-gradient-to-br from-orange-50 to-orange-100'}
                    `}>
                      
                      {/* Badge Superior */}
                      <div className={`
                        inline-block px-4 py-2 rounded-full text-sm font-bold mb-4
                        ${pkg.color === 'blue' ? 'bg-blue-600 text-white' :
                          pkg.color === 'green' ? 'bg-green-600 text-white' :
                          pkg.color === 'purple' ? 'bg-purple-600 text-white' :
                          'bg-orange-600 text-white'}
                      `}>
                        {pkg.badge}
                      </div>
                      
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">{pkg.name}</h3>
                        
                        {/* Precios con Descuentos Prominentes */}
                        <div className="mb-4">
                          {pkg.originalPrice ? (
                            <div>
                              {/* Precio Original Tachado */}
                              <div className="text-lg text-gray-400 line-through mb-1">
                                ${pkg.originalPrice.toFixed(2)} USD
                              </div>
                              
                              {/* Precio con Descuento */}
                              <div className="text-3xl font-bold text-gray-900 mb-2">
                                ${pkg.price.toFixed(2)} USD
                              </div>
                              
                              {/* Ahorros en Verde */}
                              <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold inline-block">
                                AHORRAS ${pkg.savings.toFixed(2)} USD
                              </div>
                            </div>
                          ) : (
                            <div className="text-3xl font-bold text-gray-900 mb-2">
                              ${pkg.price.toFixed(2)} USD
                            </div>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-6">
                          ${(pkg.price / pkg.cases).toFixed(2)} USD por caso
                        </div>

                        {/* Beneficios */}
                        <div className="text-left mb-6 space-y-2">
                          <div className="flex items-center text-sm text-gray-700">
                            <span className="text-green-500 mr-2">✅</span>
                            {pkg.cases} casos garantizados
                          </div>
                          <div className="flex items-center text-sm text-gray-700">
                            <span className="text-green-500 mr-2">✅</span>
                            Respuesta en 2 horas
                          </div>
                          <div className="flex items-center text-sm text-gray-700">
                            <span className="text-green-500 mr-2">✅</span>
                            Matching IA avanzado
                          </div>
                          {pkg.discount && (
                            <div className="flex items-center text-sm text-green-600 font-semibold">
                              <span className="text-green-500 mr-2">🎯</span>
                              {pkg.discount} descuento incluido
                            </div>
                          )}
                        </div>

                        {/* PayPal Buttons - Con etiquetas claras */}
                        <div className="space-y-4">
                          <PayPalScriptProvider 
                            options={{ 
                              "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                              currency: "USD",
                              intent: "capture"
                            }}
                          >
                            {/* Opción 1: Pagar CON cuenta PayPal */}
                            <div className="mb-3">
                              <div className="text-center mb-2">
                                <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                                  👤 CON cuenta PayPal
                                </span>
                              </div>
                              <PayPalButtons
                                fundingSource="paypal"
                                style={{
                                  layout: "horizontal",
                                  color: "blue",
                                  shape: "rect",
                                  label: "pay",
                                  height: 40
                                }}
                                createOrder={async () => {
                                  try {
                                    const token = localStorage.getItem('lawyer_token');
                                    const response = await fetch(`${backendUrl}/api/lawyers/create-paypal-order`, {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                      },
                                      body: JSON.stringify({
                                        package_type: pkg.id
                                      })
                                    });
                                    
                                    if (!response.ok) {
                                      const error = await response.json();
                                      throw new Error(error.detail || 'Error creando orden');
                                    }
                                    
                                    const data = await response.json();
                                    return data.order_id;
                                  } catch (error) {
                                    console.error('Error creating PayPal order:', error);
                                    alert('Error al crear la orden de pago: ' + error.message);
                                    throw error;
                                  }
                                }}
                                onApprove={async (data) => {
                                  try {
                                    const token = localStorage.getItem('lawyer_token');
                                    const response = await fetch(`${backendUrl}/api/lawyers/capture-paypal-order`, {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                      },
                                      body: JSON.stringify({
                                        order_id: data.orderID
                                      })
                                    });
                                    
                                    if (!response.ok) {
                                      const error = await response.json();
                                      throw new Error(error.detail || 'Error capturando pago');
                                    }
                                    
                                    const result = await response.json();
                                    setPaymentSuccessNotification(`+${result.credits_added} créditos agregados`);
                                    setShowPackageModal(false);
                                    await refreshLawyerData();
                                    setTimeout(() => setPaymentSuccessNotification(null), 5000);
                                  } catch (error) {
                                    console.error('Error capturing payment:', error);
                                    alert('Error al procesar el pago: ' + error.message);
                                  }
                                }}
                                onError={(err) => {
                                  console.error('PayPal error:', err);
                                  alert('Error con PayPal. Por favor intente nuevamente.');
                                }}
                              />
                            </div>

                            {/* Separador */}
                            <div className="relative my-3">
                              <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                              </div>
                              <div className="relative flex justify-center text-xs">
                                <span className="px-2 bg-white text-gray-500">o</span>
                              </div>
                            </div>

                            {/* Opción 2: Pagar SIN cuenta PayPal (tarjeta) */}
                            <div>
                              <div className="text-center mb-2">
                                <span className="inline-flex items-center bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                                  💳 SIN cuenta PayPal (tarjeta)
                                </span>
                              </div>
                              <PayPalButtons
                                fundingSource="card"
                                style={{
                                  layout: "horizontal",
                                  color: "black",
                                  shape: "rect",
                                  label: "pay",
                                  height: 40
                                }}
                                createOrder={async () => {
                                  try {
                                    const token = localStorage.getItem('lawyer_token');
                                    const response = await fetch(`${backendUrl}/api/lawyers/create-paypal-order`, {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                      },
                                      body: JSON.stringify({
                                        package_type: pkg.id
                                      })
                                    });
                                    
                                    if (!response.ok) {
                                      const error = await response.json();
                                      throw new Error(error.detail || 'Error creando orden');
                                    }
                                    
                                    const data = await response.json();
                                    return data.order_id;
                                  } catch (error) {
                                    console.error('Error creating PayPal order:', error);
                                    alert('Error al crear la orden de pago: ' + error.message);
                                    throw error;
                                  }
                                }}
                                onApprove={async (data) => {
                                  try {
                                    const token = localStorage.getItem('lawyer_token');
                                    const response = await fetch(`${backendUrl}/api/lawyers/capture-paypal-order`, {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                      },
                                      body: JSON.stringify({
                                        order_id: data.orderID
                                      })
                                    });
                                    
                                    if (!response.ok) {
                                      const error = await response.json();
                                      throw new Error(error.detail || 'Error capturando pago');
                                    }
                                    
                                    const result = await response.json();
                                    setPaymentSuccessNotification(`+${result.credits_added} créditos agregados`);
                                    setShowPackageModal(false);
                                    await refreshLawyerData();
                                    setTimeout(() => setPaymentSuccessNotification(null), 5000);
                                  } catch (error) {
                                    console.error('Error capturing payment:', error);
                                    alert('Error al procesar el pago: ' + error.message);
                                  }
                                }}
                                onError={(err) => {
                                  console.error('PayPal error:', err);
                                  alert('Error con PayPal. Por favor intente nuevamente.');
                                }}
                              />
                            </div>
                          </PayPalScriptProvider>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer con Garantías */}
              <div className="mt-8 bg-gray-50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-center text-gray-900 mb-4">🛡️ Garantía Total</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div className="flex flex-col items-center">
                    <span className="text-green-500 text-2xl mb-2">⚡</span>
                    <div className="font-semibold">Acceso Inmediato</div>
                    <div className="text-sm text-gray-600">Casos llegan en 2 horas</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-blue-500 text-2xl mb-2">🎯</span>
                    <div className="font-semibold">Casos Verificados</div>
                    <div className="text-sm text-gray-600">IA filtra alta calidad</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-purple-500 text-2xl mb-2">📞</span>
                    <div className="font-semibold">Soporte 24/7</div>
                    <div className="text-sm text-gray-600">Ayuda cuando la necesites</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center">
                <button 
                  onClick={() => setShowPackageModal(false)}
                  className="bg-gray-300 text-gray-700 px-8 py-3 rounded-xl font-semibold hover:bg-gray-400 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Update Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-8 rounded-t-3xl text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-4xl font-bold mb-2">⚙️ Modificar Perfil</h2>
                  <p className="text-green-100 text-lg">Actualiza tu información profesional</p>
                </div>
                <button 
                  onClick={() => setShowProfileModal(false)}
                  className="text-white hover:text-green-200 text-4xl font-light transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-8">
              {/* Información del Servicio */}
              <div className="text-center mb-8">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">🔄 Modificar Mi Perfil</h3>
                  <div className="text-center mb-6">
                    <div className="text-5xl font-bold text-green-600 mb-2">$19.00 USD</div>
                    <div className="text-lg text-gray-600">Pago único</div>
                  </div>
                  
                  <div className="text-left space-y-3">
                    <div className="flex items-center text-gray-700">
                      <span className="text-green-500 mr-3">✅</span>
                      Modificar áreas legales de especialización
                    </div>
                    <div className="flex items-center text-gray-700">
                      <span className="text-green-500 mr-3">✅</span>
                      Cambiar zonas/comunas de atención
                    </div>
                  </div>
                </div>

                {/* PayPal Buttons - Con las dos opciones */}
                <div className="space-y-4">
                  <PayPalScriptProvider 
                    options={{ 
                      "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                      currency: "USD",
                      intent: "capture"
                    }}
                  >
                    {/* Opción 1: CON cuenta PayPal */}
                    <div className="mb-3">
                      <div className="text-center mb-2">
                        <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                          👤 CON cuenta PayPal
                        </span>
                      </div>
                      <PayPalButtons
                        fundingSource="paypal"
                        style={{
                          layout: "horizontal",
                          color: "blue",
                          shape: "rect",
                          label: "pay",
                          height: 45
                        }}
                        createOrder={async () => {
                          try {
                            const token = localStorage.getItem('lawyer_token');
                            const response = await fetch(`${backendUrl}/api/lawyers/create-profile-update-order`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({})
                            });
                            
                            if (!response.ok) {
                              const error = await response.json();
                              throw new Error(error.detail || 'Error creando orden');
                            }
                            
                            const data = await response.json();
                            return data.order_id;
                          } catch (error) {
                            console.error('Error creating profile update order:', error);
                            alert('Error al crear la orden: ' + error.message);
                            throw error;
                          }
                        }}
                        onApprove={async (data) => {
                          try {
                            const token = localStorage.getItem('lawyer_token');
                            const response = await fetch(`${backendUrl}/api/lawyers/capture-profile-update-order`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({
                                order_id: data.orderID
                              })
                            });
                            
                            if (!response.ok) {
                              const error = await response.json();
                              throw new Error(error.detail || 'Error procesando pago');
                            }
                            
                            const result = await response.json();
                            setShowProfileModal(false);
                            alert('¡Pago exitoso! Ahora puedes modificar tu perfil.');
                            // TODO: Redirigir a formulario de edición de perfil
                          } catch (error) {
                            console.error('Error capturing payment:', error);
                            alert('Error al procesar el pago: ' + error.message);
                          }
                        }}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          alert('Error con PayPal. Por favor intente nuevamente.');
                        }}
                      />
                    </div>

                    {/* Separador */}
                    <div className="relative my-3">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="px-2 bg-white text-gray-500">o</span>
                      </div>
                    </div>

                    {/* Opción 2: SIN cuenta PayPal (tarjeta) */}
                    <div>
                      <div className="text-center mb-2">
                        <span className="inline-flex items-center bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                          💳 SIN cuenta PayPal (tarjeta)
                        </span>
                      </div>
                      <PayPalButtons
                        fundingSource="card"
                        style={{
                          layout: "horizontal",
                          color: "black",
                          shape: "rect",
                          label: "pay",
                          height: 45
                        }}
                        createOrder={async () => {
                          try {
                            const token = localStorage.getItem('lawyer_token');
                            const response = await fetch(`${backendUrl}/api/lawyers/create-profile-update-order`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({})
                            });
                            
                            if (!response.ok) {
                              const error = await response.json();
                              throw new Error(error.detail || 'Error creando orden');
                            }
                            
                            const data = await response.json();
                            return data.order_id;
                          } catch (error) {
                            console.error('Error creating profile update order:', error);
                            alert('Error al crear la orden: ' + error.message);
                            throw error;
                          }
                        }}
                        onApprove={async (data) => {
                          try {
                            const token = localStorage.getItem('lawyer_token');
                            const response = await fetch(`${backendUrl}/api/lawyers/capture-profile-update-order`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({
                                order_id: data.orderID
                              })
                            });
                            
                            if (!response.ok) {
                              const error = await response.json();
                              throw new Error(error.detail || 'Error procesando pago');
                            }
                            
                            const result = await response.json();
                            setShowProfileModal(false);
                            alert('¡Pago exitoso! Ahora puedes modificar tu perfil.');
                          } catch (error) {
                            console.error('Error capturing payment:', error);
                            alert('Error al procesar el pago: ' + error.message);
                          }
                        }}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          alert('Error con PayPal. Por favor intente nuevamente.');
                        }}
                      />
                    </div>
                  </PayPalScriptProvider>
                </div>
              </div>

              <div className="text-center mt-6">
                <button 
                  onClick={() => setShowProfileModal(false)}
                  className="bg-gray-300 text-gray-700 px-8 py-3 rounded-xl font-semibold hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Profile Modal - SIN COSTO */}
      {showViewProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 rounded-t-3xl text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold mb-2">👤 Mi Perfil Profesional</h2>
                  <p className="text-indigo-100">Información de tu cuenta</p>
                </div>
                <button 
                  onClick={() => setShowViewProfileModal(false)}
                  className="text-white hover:text-indigo-200 text-4xl font-light transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-8">
              {/* Información Personal */}
              <div className="bg-gradient-to-r from-gray-50 to-indigo-50 rounded-2xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="bg-indigo-100 p-2 rounded-lg mr-3">👤</span>
                  Información Personal
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-sm text-gray-500 mb-1">Nombre Completo</div>
                    <div className="font-semibold text-gray-900">{lawyerData.name || 'No especificado'}</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-sm text-gray-500 mb-1">Email</div>
                    <div className="font-semibold text-gray-900">{lawyerData.email || 'No especificado'}</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-sm text-gray-500 mb-1">Teléfono</div>
                    <div className="font-semibold text-gray-900">{lawyerData.phone || 'No especificado'}</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-sm text-gray-500 mb-1">RFC/CURP</div>
                    <div className="font-semibold text-gray-900">{lawyerData.rut || lawyerData.rfc || 'No especificado'}</div>
                  </div>
                </div>
              </div>

              {/* Información Profesional */}
              <div className="bg-gradient-to-r from-gray-50 to-green-50 rounded-2xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="bg-green-100 p-2 rounded-lg mr-3">⚖️</span>
                  Información Profesional
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-sm text-gray-500 mb-2">Áreas de Especialización</div>
                    <div className="flex flex-wrap gap-2">
                      {lawyerData.areas_legales && lawyerData.areas_legales.length > 0 ? (
                        lawyerData.areas_legales.map((area, index) => (
                          <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                            {area}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500">No especificadas</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-sm text-gray-500 mb-2">Cobertura Geográfica</div>
                    <div className="font-semibold text-gray-900">
                      {lawyerData.coverage_type === 'Todo Chile' || lawyerData.coverage_type === 'Todo México' 
                        ? `🌎 ${lawyerData.coverage_type}` 
                        : lawyerData.region || 'No especificada'}
                    </div>
                    {lawyerData.comunas && lawyerData.comunas.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {lawyerData.comunas.slice(0, 10).map((comuna, index) => (
                          <span key={index} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                            {comuna}
                          </span>
                        ))}
                        {lawyerData.comunas.length > 10 && (
                          <span className="text-gray-500 text-xs">+{lawyerData.comunas.length - 10} más</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-sm text-gray-500 mb-1">Años de Experiencia</div>
                    <div className="font-semibold text-gray-900">{lawyerData.experience_years || 'No especificado'} años</div>
                  </div>
                </div>
              </div>

              {/* Estadísticas */}
              <div className="bg-gradient-to-r from-gray-50 to-purple-50 rounded-2xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="bg-purple-100 p-2 rounded-lg mr-3">📊</span>
                  Estadísticas
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                    <div className="text-3xl font-bold text-green-600">{lawyerData.credits_remaining || 0}</div>
                    <div className="text-sm text-gray-500">Créditos</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                    <div className="text-3xl font-bold text-blue-600">{lawyerData.total_cases_taken || 0}</div>
                    <div className="text-sm text-gray-500">Casos Tomados</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                    <div className="text-3xl font-bold text-purple-600">{lawyerData.cases_this_month || 0}</div>
                    <div className="text-sm text-gray-500">Este Mes</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                    <div className="text-3xl font-bold text-indigo-600">
                      {lawyerData.verified ? '✅' : '⏳'}
                    </div>
                    <div className="text-sm text-gray-500">Verificado</div>
                  </div>
                </div>
              </div>

              {/* Botones de Acción */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button 
                  onClick={() => {
                    setShowViewProfileModal(false);
                    setShowProfileModal(true);
                  }}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300"
                >
                  ⚙️ Modificar Perfil ($19 USD)
                </button>
                <button 
                  onClick={() => setShowViewProfileModal(false)}
                  className="bg-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-400 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Take Case Modal */}
      {showTakeCaseModal && selectedCase && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={() => setShowTakeCaseModal(false)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 rounded-t-3xl text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">⚖️ Tomar Caso</h2>
                  <p className="text-green-100">Caso ID: {selectedCase.id.slice(0, 8)}...</p>
                </div>
                <button 
                  onClick={() => setShowTakeCaseModal(false)}
                  className="text-white hover:text-green-200 text-4xl font-bold transition-colors bg-black bg-opacity-20 hover:bg-opacity-30 rounded-full w-10 h-10 flex items-center justify-center"
                  aria-label="Cerrar modal"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Case Summary */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-2">📋 Resumen del Caso</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <div><strong>Área:</strong> {selectedCase.area_legal}</div>
                  <div><strong>Ubicación:</strong> {selectedCase.comuna}</div>
                  <div><strong>Cliente:</strong> {selectedCase.name}</div>
                  <div><strong>IA Score:</strong> {selectedCase.quality_score?.quality_score || 'N/A'}%</div>
                </div>
              </div>

              {/* Payment Options */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 text-center">Selecciona método de pago:</h3>
                
                {/* Credits Option */}
                <div className="border-2 border-green-200 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-green-800">🎫 Usar Créditos</h4>
                    <div className="text-lg font-bold text-green-600">
                      {lawyerData.credits_remaining || 0} disponibles
                    </div>
                  </div>
                  {(lawyerData.credits_remaining || 0) > 0 ? (
                    <button
                      onClick={takeCaseWithCredits}
                      disabled={takingCase}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50"
                    >
                      {takingCase ? '⏳ Procesando...' : '✅ Usar 1 Crédito'}
                    </button>
                  ) : (
                    <div className="text-center p-3 bg-gray-100 rounded-lg">
                      <div className="text-gray-600 mb-3">Sin créditos disponibles</div>
                      <button
                        onClick={() => {
                          setShowTakeCaseModal(false);
                          setShowPackageModal(true);
                        }}
                        className="bg-yellow-500 hover:bg-yellow-600 text-yellow-900 font-bold py-2 px-4 rounded-lg transition-colors duration-200"
                      >
                        🛒 Comprar Créditos
                      </button>
                    </div>
                  )}
                </div>

                {/* Direct Payment Option */}
                <div className="border-2 border-blue-200 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-blue-800">💳 Pago Directo</h4>
                    <div className="text-lg font-bold text-blue-600">$4.70 USD</div>
                  </div>
                  <button
                    onClick={takeCaseWithPayment}
                    disabled={takingCase}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {takingCase ? '⏳ Procesando...' : '💳 Pagar $4.70 USD'}
                  </button>
                </div>
              </div>

              {/* Competition Info */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <div className="text-sm text-yellow-800">
                  Solo los primeros <strong>3 abogados</strong> que paguen acceden al caso
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Case Details Modal */}
      {showCaseDetailsModal && selectedCase && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCaseDetailsModal(false)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-3xl flex justify-between items-center">
              <h3 className="text-2xl font-bold">📋 Detalles del Caso</h3>
              <button 
                onClick={() => setShowCaseDetailsModal(false)}
                className="text-white hover:text-blue-200 text-4xl font-bold transition-colors bg-black bg-opacity-20 hover:bg-opacity-30 rounded-full w-10 h-10 flex items-center justify-center"
                aria-label="Cerrar modal"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Información del Caso */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-bold text-lg mb-3 text-gray-800">⚖️ Información Legal</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><strong>Área Legal:</strong> {selectedCase.area_legal}</div>
                  <div><strong>Urgencia:</strong> {selectedCase.urgency || 'Media'}</div>
                  <div><strong>Región:</strong> {selectedCase.region}</div>
                  <div><strong>Comuna:</strong> {selectedCase.comuna}</div>
                  <div><strong>Presupuesto:</strong> {selectedCase.budget_range || 'No especificado'}</div>
                  <div><strong>Consulta Previa:</strong> {selectedCase.previous_consultations || 'No'}</div>
                </div>
              </div>

              {/* Descripción del Problema */}
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-bold text-lg mb-3 text-blue-800">📝 Descripción del Caso</h4>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedCase.description}</p>
              </div>

              {/* Análisis de Documentos */}
              <div className="bg-green-50 rounded-xl p-4">
                <h4 className="font-bold text-lg mb-3 text-green-800">📁 Documentos Adjuntos</h4>
                <div className="text-gray-700">
                  {analyzeAttachments(selectedCase.attachments).analysis.split('\n').map((line, index) => (
                    <div key={index} className={index === 0 ? "font-semibold mb-2" : "ml-2"}>
                      {line}
                    </div>
                  ))}
                </div>
              </div>

              {/* Métricas de IA */}
              <div className="bg-yellow-50 rounded-xl p-4">
                <h4 className="font-bold text-lg mb-3 text-yellow-800">🤖 Análisis de IA</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><strong>Calidad:</strong> {selectedCase.quality_score?.quality_score || 'N/A'}/100</div>
                  <div><strong>Verificación:</strong> {selectedCase.verification_score?.total_score || 'N/A'}%</div>
                  <div><strong>Fecha:</strong> {new Date(selectedCase.created_at).toLocaleDateString('es-CL')}</div>
                  <div><strong>Modalidad:</strong> {selectedCase.procedure_type === 'remote_possible' ? 'Remoto posible' : 'Presencial'}</div>
                </div>
              </div>

              {/* Botón para Tomar Caso */}
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => {
                    setShowCaseDetailsModal(false);
                    handleTakeCase(selectedCase);
                  }}
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-300 transform hover:scale-105"
                >
                  💼 Tomar Este Caso
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🔥 MODAL DE DETALLES DE CASO TOMADO */}
      {showTakenCaseDetailsModal && selectedTakenCase && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowTakenCaseDetailsModal(false)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del Modal */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-t-3xl">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-3xl font-bold">📋 Caso Tomado - Información Completa</h3>
                  <p className="text-purple-100 mt-1">
                    Todos los detalles del cliente y documentos del caso
                  </p>
                </div>
                <button 
                  onClick={() => setShowTakenCaseDetailsModal(false)}
                  className="text-white hover:text-purple-200 text-4xl font-bold transition-colors bg-black bg-opacity-20 hover:bg-opacity-30 rounded-full w-12 h-12 flex items-center justify-center"
                  aria-label="Cerrar modal"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-8 space-y-8">
              {/* Información del Cliente - ACCESO COMPLETO */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <h4 className="font-bold text-2xl mb-4 text-blue-800 flex items-center">
                  👤 Información del Cliente
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Nombre Completo</label>
                      <div className="bg-white p-3 rounded-lg border text-lg font-semibold">
                        {selectedTakenCase.name || 'No especificado'}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">RUT</label>
                      <div className="bg-white p-3 rounded-lg border">
                        {selectedTakenCase.rut || 'No especificado'}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Región</label>
                      <div className="bg-white p-3 rounded-lg border">
                        📍 {selectedTakenCase.region || 'No especificado'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Email</label>
                      <div className="bg-white p-3 rounded-lg border flex items-center justify-between">
                        <span>{selectedTakenCase.email || 'No especificado'}</span>
                        {selectedTakenCase.email && (
                          <a
                            href={generateContactLinks(selectedTakenCase).emailLink}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm font-semibold transition-colors"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            📧 Enviar Email
                          </a>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Teléfono</label>
                      <div className="bg-white p-3 rounded-lg border flex items-center justify-between">
                        <span>{selectedTakenCase.phone || 'No especificado'}</span>
                        {selectedTakenCase.phone && (
                          <div className="flex gap-2">
                            <a
                              href={generateContactLinks(selectedTakenCase).whatsappLink}
                              className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg text-sm font-semibold transition-colors"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              💬 WhatsApp
                            </a>
                            <a
                              href={generateContactLinks(selectedTakenCase).callLink}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm font-semibold transition-colors"
                            >
                              📱 Llamar
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Comuna</label>
                      <div className="bg-white p-3 rounded-lg border">
                        🏘️ {selectedTakenCase.comuna || 'No especificado'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Información del Caso */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
                <h4 className="font-bold text-2xl mb-4 text-orange-800 flex items-center">
                  ⚖️ Detalles del Caso Legal
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-xl border">
                    <div className="text-sm font-semibold text-gray-600">Área Legal</div>
                    <div className="text-lg font-bold text-orange-800">{selectedTakenCase.area_legal}</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border">
                    <div className="text-sm font-semibold text-gray-600">Urgencia</div>
                    <div className="text-lg font-bold text-orange-800">{selectedTakenCase.urgency || 'Media'}</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border">
                    <div className="text-sm font-semibold text-gray-600">Presupuesto</div>
                    <div className="text-lg font-bold text-orange-800">{selectedTakenCase.budget_range || 'No especificado'}</div>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-xl border">
                  <h5 className="font-bold text-lg mb-3 text-gray-800">📝 Descripción Completa del Caso</h5>
                  <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedTakenCase.description || 'Sin descripción disponible'}
                  </div>
                </div>
              </div>

              {/* Documentos Adjuntos */}
              {selectedTakenCase.attachments && selectedTakenCase.attachments.length > 0 && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                  <h4 className="font-bold text-2xl mb-4 text-green-800 flex items-center">
                    📎 Documentos Adjuntos ({selectedTakenCase.attachments.length})
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedTakenCase.attachments.map((attachment, index) => (
                      <div key={index} className="bg-white p-4 rounded-xl border border-green-200 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="bg-green-100 p-2 rounded-lg">
                              📄
                            </div>
                            <div>
                              <div className="font-semibold text-gray-800">
                                {attachment.name || `Documento ${index + 1}`}
                              </div>
                              <div className="text-sm text-gray-600">
                                {attachment.type || 'Tipo de archivo desconocido'}
                              </div>
                            </div>
                          </div>
                          
                          {attachment.data && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  // Crear y descargar archivo
                                  const link = document.createElement('a');
                                  link.href = attachment.data;
                                  link.download = attachment.name || `documento_${index + 1}`;
                                  link.click();
                                }}
                                className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg text-sm font-semibold transition-colors"
                              >
                                ⬇️ Descargar
                              </button>
                              
                              {attachment.type && attachment.type.includes('image') && (
                                <button
                                  onClick={() => {
                                    window.open(attachment.data, '_blank');
                                  }}
                                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm font-semibold transition-colors"
                                >
                                  👁️ Ver
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Información de Pago y Contacto */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-200">
                <h4 className="font-bold text-2xl mb-4 text-gray-800 flex items-center">
                  💳 Información de Transacción
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-xl border">
                    <div className="text-sm font-semibold text-gray-600">Fecha de Contacto</div>
                    <div className="text-lg font-bold text-gray-800">
                      {selectedTakenCase.contact_date ? 
                        new Date(selectedTakenCase.contact_date).toLocaleDateString('es-CL', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 
                        'No disponible'
                      }
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-xl border">
                    <div className="text-sm font-semibold text-gray-600">Método de Pago</div>
                    <div className="text-lg font-bold text-gray-800">
                      {selectedTakenCase.payment_method === 'credit' ? '🎫 Créditos' : '💰 Pago Directo'}
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-xl border">
                    <div className="text-sm font-semibold text-gray-600">Puntaje IA</div>
                    <div className="text-lg font-bold text-gray-800">
                      🤖 {selectedTakenCase.ia_score || 'N/A'}/100
                    </div>
                  </div>
                </div>
              </div>

              {/* Botones de Acción Rápida */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <h4 className="font-bold text-2xl mb-4 text-indigo-800 text-center">
                  🚀 Acciones Rápidas
                </h4>
                
                <div className="flex flex-wrap justify-center gap-4">
                  {selectedTakenCase.email && (
                    <a
                      href={generateContactLinks(selectedTakenCase).emailLink}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      📧 Enviar Email Profesional
                    </a>
                  )}
                  
                  {selectedTakenCase.phone && (
                    <>
                      <a
                        href={generateContactLinks(selectedTakenCase).whatsappLink}
                        className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        💬 Contactar por WhatsApp
                      </a>
                      
                      <a
                        href={generateContactLinks(selectedTakenCase).callLink}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2"
                      >
                        📱 Llamar Directamente
                      </a>
                    </>
                  )}
                </div>
                
                <div className="text-center mt-4">
                  <p className="text-indigo-600 text-sm">
                    💡 <strong>Tip:</strong> Contacta al cliente dentro de las primeras 24 horas para mejores resultados
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleLawyerDashboard;